import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { BoardMember, CreateBoardMemberData } from '../../types/board';

interface BoardMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (memberData: CreateBoardMemberData) => Promise<{ data: any; error: string | null }>;
  member?: BoardMember;
  isEditing?: boolean;
}



export function BoardMemberModal({ open, onClose, onSave, member, isEditing = false }: BoardMemberModalProps) {
  const [formData, setFormData] = useState<CreateBoardMemberData>({
    name: '',
    role: '',
    personality: {
      traits: [],
      communication_style: '',
      decision_making_approach: '',
      background_story: '',
    },
    expertise: [],
    background: '',
  });
  const [newTrait, setNewTrait] = useState('');
  const [newExpertise, setNewExpertise] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (member && isEditing) {
      setFormData({
        name: member.name,
        role: member.role || '',
        personality: member.personality,
        expertise: member.expertise,
        background: member.background || '',
      });
    } else {
      setFormData({
        name: '',
        role: '',
        personality: {
          traits: [],
          communication_style: '',
          decision_making_approach: '',
          background_story: '',
        },
        expertise: [],
        background: '',
      });
    }
  }, [member, isEditing, open]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await onSave(formData);
      if (result.error) {
        setError(result.error);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save board member');
    } finally {
      setLoading(false);
    }
  };

  const addTrait = () => {
    if (newTrait.trim() && !formData.personality.traits.includes(newTrait.trim())) {
      setFormData(prev => ({
        ...prev,
        personality: {
          ...prev.personality,
          traits: [...prev.personality.traits, newTrait.trim()]
        }
      }));
      setNewTrait('');
    }
  };

  const removeTrait = (trait: string) => {
    setFormData(prev => ({
      ...prev,
      personality: {
        ...prev.personality,
        traits: prev.personality.traits.filter(t => t !== trait)
      }
    }));
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !formData.expertise.includes(newExpertise.trim())) {
      setFormData(prev => ({
        ...prev,
        expertise: [...prev.expertise, newExpertise.trim()]
      }));
      setNewExpertise('');
    }
  };

  const removeExpertise = (expertise: string) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.filter(e => e !== expertise)
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isEditing ? 'Edit Board Member' : 'Add Board Member'}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Role"
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              placeholder="e.g., Technology Advisor"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Personality Traits
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {formData.personality.traits.map((trait) => (
                <Chip
                  key={trait}
                  label={trait}
                  onDelete={() => removeTrait(trait)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Add trait"
                value={newTrait}
                onChange={(e) => setNewTrait(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTrait()}
              />
              <Button
                variant="outlined"
                onClick={addTrait}
                disabled={!newTrait.trim()}
              >
                Add
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Communication Style"
              value={formData.personality.communication_style}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                personality: { ...prev.personality, communication_style: e.target.value }
              }))}
              placeholder="e.g., Direct and analytical"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Decision Making Approach"
              value={formData.personality.decision_making_approach}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                personality: { ...prev.personality, decision_making_approach: e.target.value }
              }))}
              placeholder="e.g., Data-driven and systematic"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Background Story"
              value={formData.personality.background_story}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                personality: { ...prev.personality, background_story: e.target.value }
              }))}
              placeholder="Brief background story for this board member..."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Areas of Expertise
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {formData.expertise.map((expertise) => (
                <Chip
                  key={expertise}
                  label={expertise}
                  onDelete={() => removeExpertise(expertise)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Add expertise"
                value={newExpertise}
                onChange={(e) => setNewExpertise(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addExpertise()}
              />
              <Button
                variant="outlined"
                onClick={addExpertise}
                disabled={!newExpertise.trim()}
              >
                Add
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Background"
              value={formData.background}
              onChange={(e) => setFormData(prev => ({ ...prev, background: e.target.value }))}
              placeholder="Additional background information..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !formData.name.trim()}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 